import * as React from 'react';
import { Check, RotateCcw, X } from 'lucide-react';
import { CitationChip } from '@/components/ui/citation-chip';
import { Spinner } from '@/components/ui/spinner';
import { WorkspaceButton } from '@/components/ui/workspace';
import { cn } from '@/lib/utils/cn';
import { TokenText } from './block-text';
import type { TokenValues } from './tokens';
import type { EditorCitation, RewriteProposal } from './types';
import { diffWords, sideOf, type DiffPart } from './word-diff';

/**
 * A rewrite proposal beside the block's current text (E14-design §5), the
 * changes marked word by word: what the proposal drops is struck through
 * on the left, what it adds is marked on the right. Figures stay chips.
 * Nothing is saved before Accept.
 */
export interface ProposalCompareProps {
  current: string;
  proposal: RewriteProposal;
  /** The preset or instruction it answers, shown over the proposal. */
  instruction: string;
  tokens: TokenValues;
  tokenSource?: string;
  /** Accept is saving, or another call is under way. */
  busy?: boolean;
  accepting?: boolean;
  onAccept: () => void;
  onTryAgain: () => void;
  onDiscard: () => void;
  onOpenCitation?: (citation: EditorCitation) => void;
}

function DiffText({
  parts,
  tokens,
  source,
}: {
  parts: DiffPart[];
  tokens: TokenValues;
  source?: string;
}) {
  return (
    <p className="m-0 whitespace-pre-wrap text-body leading-relaxed text-ink-900">
      {parts.map((p, i) => {
        const inner = <TokenText text={p.text} tokens={tokens} source={source} />;
        if (p.kind === 'removed') {
          return (
            <del
              key={i}
              className="rounded-[3px] bg-posture-critical-tint text-posture-critical decoration-1"
            >
              {inner}
            </del>
          );
        }
        if (p.kind === 'added') {
          return (
            <ins
              key={i}
              className="rounded-[3px] bg-posture-healthy-tint text-posture-healthy no-underline"
            >
              {inner}
            </ins>
          );
        }
        return <React.Fragment key={i}>{inner}</React.Fragment>;
      })}
    </p>
  );
}

export function ProposalCompare({
  current,
  proposal,
  instruction,
  tokens,
  tokenSource,
  busy = false,
  accepting = false,
  onAccept,
  onTryAgain,
  onDiscard,
  onOpenCitation,
}: ProposalCompareProps) {
  const parts = React.useMemo(() => diffWords(current, proposal.text), [current, proposal.text]);
  const unchanged = parts.every((p) => p.kind === 'same');
  return (
    <div className="flex flex-col gap-3" data-testid="proposal-compare">
      <div className="grid gap-3 md:grid-cols-2">
        <section aria-label="Current text" className="flex min-w-0 flex-col gap-1.5">
          <h4 className="m-0 text-meta font-semibold uppercase tracking-wide text-ink-500">Now</h4>
          <div className="rounded-card border border-ink-100 bg-paper-0 px-3 py-2.5">
            <DiffText parts={sideOf(parts, 'before')} tokens={tokens} source={tokenSource} />
          </div>
        </section>
        <section aria-label="Proposed text" className="flex min-w-0 flex-col gap-1.5">
          <h4 className="m-0 truncate text-meta font-semibold uppercase tracking-wide text-ai-ink">
            Proposed · {instruction}
          </h4>
          <div
            className={cn(
              'flex flex-col gap-2 rounded-card border border-ai-surface-border bg-paper-0 px-3 py-2.5',
            )}
          >
            <DiffText parts={sideOf(parts, 'after')} tokens={tokens} source={tokenSource} />
            {proposal.citations.length > 0 ? (
              <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
                {proposal.citations.map((c) => (
                  <li key={c.id} className="min-w-0">
                    <CitationChip
                      cite={{
                        id: c.id,
                        doc_label: c.label,
                        confidence: c.confidence ?? 1,
                      }}
                      showConfidence={c.confidence !== undefined}
                      onOpen={() => onOpenCitation?.(c)}
                    />
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </section>
      </div>
      {unchanged ? (
        <p className="m-0 text-meta text-ink-500">The proposal is the same as the current text.</p>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <WorkspaceButton variant="primary" disabled={busy || unchanged} onClick={onAccept}>
          {accepting ? (
            <Spinner className="h-3.5 w-3.5" />
          ) : (
            <Check className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          Accept
        </WorkspaceButton>
        <WorkspaceButton disabled={busy} onClick={onTryAgain}>
          <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
          Try again
        </WorkspaceButton>
        <WorkspaceButton variant="tertiary" disabled={accepting} onClick={onDiscard}>
          <X className="h-3.5 w-3.5" aria-hidden="true" />
          Discard
        </WorkspaceButton>
      </div>
    </div>
  );
}
